import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack } from '@mui/material';
import { postPricingPreview } from '../api/pricingApi';
import { typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { Button } from '../components/ui/Button';
import { Select, SelectOption } from '../components/ui/Select';
import { TextInput } from '../components/ui/TextInput';

function formatAmount(amount, currency, t) {
  if (amount === null || amount === undefined) {
    return t('common:empty_value');
  }
  return t('pricing:currency_amount', { amount, currency: currency || 'EUR' });
}

export function BillingPricingPreviewPage() {
  const { t } = useTranslation(['pricing', 'common']);
  const [modulesText, setModulesText] = useState('');
  const [usersCount, setUsersCount] = useState(10);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const emptyValue = t('common:empty_value');

  const handleCalculate = async () => {
    const modules = modulesText
      .split(/[\n,]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    if (modules.length === 0) {
      setError(t('pricing:pricing_preview.modules_required'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const preview = await postPricingPreview({
        modules,
        users_count: Number(usersCount),
        billing_interval: billingInterval,
      });
      setResult(preview);
    } catch {
      setError(t('pricing:pricing_preview.load_failed'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const lineItems = result?.line_items || [];

  const metricCards = useMemo(() => {
    if (!result) {
      return [];
    }

    return [
      {
        label: t('pricing:pricing_preview.user_tier'),
        value: result.user_tier_label || emptyValue,
      },
      {
        label: t('pricing:pricing_preview.user_tier_multiplier'),
        value: result.user_tier_multiplier ?? emptyValue,
      },
      {
        label: t('pricing:pricing_preview.monthly_total'),
        value: formatAmount(result.monthly_total, result.currency, t),
      },
      {
        label: t('pricing:pricing_preview.yearly_total'),
        value: formatAmount(result.yearly_total, result.currency, t),
      },
      {
        label: t('pricing:pricing_preview.annual_discount'),
        value: formatAmount(result.annual_discount_amount, result.currency, t),
      },
      {
        label: t('pricing:pricing_preview.amount_due'),
        value: formatAmount(result.amount_due_for_selected_interval, result.currency, t),
      },
      {
        label: t('pricing:pricing_preview.commercial_discount'),
        value: formatAmount(result.commercial_discount_amount ?? 0, result.currency, t),
      },
      {
        label: t('pricing:pricing_preview.final_amount'),
        value: formatAmount(result.final_amount ?? result.amount_due_for_selected_interval, result.currency, t),
      },
    ];
  }, [emptyValue, result, t]);

  const lineItemColumns = useMemo(
    () => [
      {
        id: 'module',
        label: t('pricing:pricing_preview.line_module'),
        render: (item) => item.name || item.canonical_code || emptyValue,
      },
      {
        id: 'monthly_price',
        label: t('pricing:pricing_preview.line_monthly_price'),
        render: (item) => formatAmount(item.monthly_price, item.currency, t),
      },
      {
        id: 'monthly_amount',
        label: t('pricing:pricing_preview.line_monthly_amount'),
        render: (item) => formatAmount(item.monthly_amount, item.currency, t),
      },
      {
        id: 'yearly_amount',
        label: t('pricing:pricing_preview.line_yearly_amount'),
        render: (item) => formatAmount(item.yearly_amount, item.currency, t),
      },
    ],
    [emptyValue, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('pricing:pricing_preview.title')}
        subtitle={t('pricing:pricing_preview.subtitle')}
        actions={(
          <Button variant="Secondary" component={RouterLink} to="/billing">
            {t('common:dashboard')}
          </Button>
        )}
      />

      <Stack spacing={2.5}>
        <SectionCard variant="Default" sx={{ mb: 0 }}>
          <Stack spacing={2}>
            <TextInput
              label={t('pricing:pricing_preview.modules_label')}
              value={modulesText}
              onChange={(e) => setModulesText(e.target.value)}
              multiline
              minRows={4}
              spellCheck={false}
              placeholder={'quality\npriority\nschooling'}
              InputProps={{
                sx: {
                  fontFamily: typographyTokens.fontFamilyMono || 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 13,
                },
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 2,
                width: '100%',
              }}
            >
              <TextInput
                type="number"
                label={t('pricing:pricing_preview.users_count')}
                value={usersCount}
                onChange={(e) => setUsersCount(e.target.value)}
                inputProps={{ min: 1 }}
              />
              <Select
                fullWidth
                label={t('pricing:pricing_preview.billing_interval')}
                value={billingInterval}
                onChange={(e) => setBillingInterval(e.target.value)}
              >
                <SelectOption value="monthly">
                  {t('pricing:pricing_preview.interval_monthly')}
                </SelectOption>
                <SelectOption value="yearly">
                  {t('pricing:pricing_preview.interval_yearly')}
                </SelectOption>
              </Select>
            </Box>
            <Box>
              <Button
                variant="Primary"
                onClick={handleCalculate}
                loading={loading}
                disabled={loading}
              >
                {loading
                  ? t('pricing:pricing_preview.calculating')
                  : t('pricing:pricing_preview.calculate')}
              </Button>
            </Box>
          </Stack>
        </SectionCard>

        {error && (
          <Alert severity="error">
            {error}
          </Alert>
        )}

        {result && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
                width: '100%',
              }}
            >
              {metricCards.map((card) => (
                <MetricCard
                  key={card.label}
                  value={card.value}
                  label={card.label}
                />
              ))}
            </Box>

            {(result.invalid_modules || []).length > 0 && (
              <SectionCard variant="Warning" sx={{ mb: 0 }}>
                <Alert severity="warning" sx={{ bgcolor: 'transparent', p: 0 }}>
                  {t('pricing:pricing_preview.invalid_modules')}
                  {': '}
                  {(result.invalid_modules || []).join(', ')}
                </Alert>
              </SectionCard>
            )}

            {(result.warnings || []).map((warning) => (
              <SectionCard key={warning} variant="Warning" sx={{ mb: 0 }}>
                <Alert severity="warning" sx={{ bgcolor: 'transparent', p: 0 }}>
                  {warning}
                </Alert>
              </SectionCard>
            ))}

            <SectionCard
              variant="Default"
              title={t('pricing:pricing_preview.line_items')}
              noPadding
              sx={{ mb: 0 }}
            >
              <DataTable
                type="Default"
                embedded
                columns={lineItemColumns}
                rows={lineItems}
                getRowKey={(item) => item.canonical_code || item.public_code || item.name}
                emptyMessage={t('pricing:pricing_preview.no_line_items')}
                sx={{
                  overflowX: 'auto',
                  '& table': { minWidth: '100%' },
                }}
              />
            </SectionCard>
          </>
        )}
      </Stack>
    </Box>
  );
}
